import { PencilIcon, SaveIcon, SplitIcon, XIcon } from 'lucide-react';

import React, { type ReactNode, useState } from 'react';

import { switchConfirmKey } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import { SplitMethod, formatAmountForCurrency } from '@splitfiesta/shared';
import { Avatar, Button, Card } from '@splitfiesta/ui';

import { ConfirmationDialog } from '@/components/common/ConfirmationDialog';

import {
  Split,
  SplitAmountCalculator,
  SplitMemberList,
  SplitSummary,
  SplitTypeSelector,
  useSplitCalculation,
} from './split';

interface UnifiedExpenseSplitProps {
  splits: Split[];
  setSplits: (splits: Split[]) => void;
  splitMethod: SplitMethod;
  setSplitMethod: (method: SplitMethod) => void;
  totalAmount: number;
  currency: string;
  currentUserId?: string;
  /** Spots saved during this form session; the member list keeps them last. */
  newSpotIds?: readonly string[];
  isLoading?: boolean;
  onSave?: () => void;
  isSaving?: boolean;
  canSave?: boolean;
  mode?: 'create' | 'edit';
  /** Offers "Split by item" in the method selector. */
  itemsAllowed?: boolean;
  /** Split by item is the active mode; `itemsContent` replaces the member list. */
  itemsMode?: boolean;
  /** A non-empty list asks before another method replaces it. */
  itemCount?: number;
  onSelectItems?: () => void;
  itemsContent?: ReactNode;
  /** After the last member: adds someone who is not in the group yet. */
  saveSpot?: ReactNode;
}

export function UnifiedExpenseSplit({
  splits,
  setSplits,
  splitMethod,
  setSplitMethod,
  totalAmount,
  currency,
  currentUserId,
  newSpotIds,
  isLoading = false,
  onSave,
  isSaving = false,
  canSave = true,
  mode: _mode = 'create',
  itemsAllowed = false,
  itemsMode = false,
  itemCount = 0,
  onSelectItems,
  itemsContent,
  saveSpot,
}: UnifiedExpenseSplitProps) {
  const { t } = useTranslation();
  const [showMethodDropdown, setShowMethodDropdown] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  // Kept after confirm so the dialog's title survives its leave transition.
  const [switchTo, setSwitchTo] = useState<SplitMethod | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const chooseMethod = (method: SplitMethod) => {
    if (itemsMode && itemCount > 0) {
      setSwitchTo(method);
      setConfirmOpen(true);
      return;
    }
    setSplitMethod(method);
  };

  const itemsOption =
    itemsAllowed || itemsMode
      ? {
          label: t('expenses:splitMethods.lineItems'),
          selected: itemsMode,
          onSelect: () => onSelectItems?.(),
        }
      : undefined;

  const selector = (
    <SplitTypeSelector
      splitMethod={splitMethod}
      setSplitMethod={chooseMethod}
      showDropdown={showMethodDropdown}
      setShowDropdown={setShowMethodDropdown}
      isLoading={isLoading}
      itemsOption={itemsOption}
    />
  );

  const switchConfirm = (
    <ConfirmationDialog
      isOpen={confirmOpen}
      onClose={() => setConfirmOpen(false)}
      onConfirm={() => {
        setConfirmOpen(false);
        if (switchTo) setSplitMethod(switchTo);
      }}
      title={
        switchTo ? t(`expenses:lineItems.${switchConfirmKey(switchTo)}`) : ''
      }
      description={t('expenses:lineItems.switchBody')}
      confirmText={t('expenses:lineItems.switch')}
      cancelText={t('common:actions.cancel')}
      variant="warning"
    />
  );

  const {
    numericAmount,
    includedSplits,
    calculationResult,
    getEqualSplitAmount,
    getSharesSplitAmount,
    getEffectiveShares,
    handleIncludeToggle,
    handlePercentageChange,
    handlePercentageSliderChange,
    handleAmountChange,
    handleSharesChange,
    handleSplitEvenly,
    handleAutoComplete,
  } = useSplitCalculation(
    splits,
    setSplits,
    splitMethod,
    totalAmount,
    currency
  );

  // Get simplified method label for collapsed view
  const getCollapsedMethodLabel = () => {
    switch (splitMethod) {
      case 'equal':
        return t('expenses:splitMethods.collapsedEqual', 'pays the same');
      case 'percentage':
        return t(
          'expenses:splitMethods.collapsedPercentage',
          'pays a percentage'
        );
      case 'shares':
        return t('expenses:splitMethods.collapsedShares', 'pays by shares');
      case 'custom':
        return t(
          'expenses:splitMethods.collapsedCustom',
          'pays a custom amount'
        );
      default:
        return '';
    }
  };

  // Get display amount for collapsed view based on split method
  const getCollapsedDisplayAmount = () => {
    if (numericAmount === 0 || includedSplits.length === 0) return '0';

    if (splitMethod === 'equal') {
      return formatAmountForCurrency(
        getEqualSplitAmount(includedSplits[0]?.userId || ''),
        currency
      );
    } else if (splitMethod === 'percentage') {
      // For percentage, show range if amounts vary
      const amounts = includedSplits
        .map(split => ((split.percentage || 0) / 100) * numericAmount)
        .filter(amt => amt > 0);

      if (amounts.length === 0) return formatAmountForCurrency(0, currency);

      const minAmount = Math.min(...amounts);
      const maxAmount = Math.max(...amounts);

      if (Math.abs(minAmount - maxAmount) < 0.01) {
        return formatAmountForCurrency(minAmount, currency);
      } else {
        return `${formatAmountForCurrency(minAmount, currency)} - ${formatAmountForCurrency(maxAmount, currency)}`;
      }
    } else if (splitMethod === 'shares') {
      // For shares, show range if amounts vary
      const amounts = includedSplits
        .map(split => getSharesSplitAmount(split.userId))
        .filter(amt => amt > 0);

      if (amounts.length === 0) return formatAmountForCurrency(0, currency);

      const minAmount = Math.min(...amounts);
      const maxAmount = Math.max(...amounts);

      if (Math.abs(minAmount - maxAmount) < 0.01) {
        return formatAmountForCurrency(minAmount, currency);
      } else {
        return `${formatAmountForCurrency(minAmount, currency)} - ${formatAmountForCurrency(maxAmount, currency)}`;
      }
    } else {
      // For custom amounts, show range if amounts vary
      const amounts = includedSplits
        .map(split => split.amount || 0)
        .filter(amt => amt > 0);

      if (amounts.length === 0) return formatAmountForCurrency(0, currency);

      const minAmount = Math.min(...amounts);
      const maxAmount = Math.max(...amounts);

      if (Math.abs(minAmount - maxAmount) < 0.01) {
        return formatAmountForCurrency(minAmount, currency);
      } else {
        return `${formatAmountForCurrency(minAmount, currency)} - ${formatAmountForCurrency(maxAmount, currency)}`;
      }
    }
  };

  if (itemsMode) {
    return (
      <div className="space-y-3">
        <Card className="space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <SplitIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('expenses:sections.splitMethodSection')}
              </h3>
            </div>
            {selector}
          </div>
          {itemsContent}
        </Card>
        {switchConfirm}
      </div>
    );
  }

  // Compact view when no amount or collapsed
  if (numericAmount === 0 || !isExpanded) {
    const isClickable = numericAmount > 0 && !isLoading;
    return (
      <div className="space-y-3">
        {/* Use a div wrapper for click handling to avoid nested buttons */}
        <div
          role={isClickable ? 'button' : undefined}
          tabIndex={isClickable ? 0 : undefined}
          className={isClickable ? 'cursor-pointer' : ''}
          onClick={() => isClickable && setIsExpanded(true)}
          onKeyDown={e => {
            if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              setIsExpanded(true);
            }
          }}
        >
          <Card
            className={`space-y-3 ${isClickable ? 'hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors' : ''}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <SplitIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('expenses:sections.splitMethodSection')}
                </h3>
              </div>
              {numericAmount > 0 && (
                <Button
                  variant="primary"
                  size="sm"
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    setIsExpanded(true);
                  }}
                  disabled={isLoading}
                  leftIcon={<PencilIcon className="h-4 w-4" />}
                  className="shrink-0"
                >
                  {t('common:actions.edit')}
                </Button>
              )}
            </div>

            {/* Compact split info */}
            {numericAmount === 0 ? (
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                  {t('expenses:messages.enterAmountToSplit')}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Orange method label on its own row */}
                <span className="text-orange-600 dark:text-orange-400 font-medium block">
                  {getCollapsedMethodLabel()}
                </span>
                {/* Avatars and member count */}
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 shrink-0">
                    {includedSplits.slice(0, 3).map(split => (
                      <Avatar
                        key={split.userId}
                        name={split.userName}
                        src={split.profileImage}
                        size="xs"
                      />
                    ))}
                    {includedSplits.length > 3 && (
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-700 text-xs font-medium text-gray-700 dark:text-gray-300">
                        +{includedSplits.length - 3}
                      </div>
                    )}
                  </div>
                  <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {includedSplits.length} {t('common:labels.members')}
                  </span>
                </div>
                {/* Amount on its own row */}
                <div className="text-sm font-bold text-gray-900 dark:text-gray-100">
                  {getCollapsedDisplayAmount()}
                  {splitMethod === 'equal' && ` ${t('common:labels.each')}`}
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Save button outside the card */}
      {onSave && (
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="md"
            type="button"
            onClick={onSave}
            disabled={!canSave || isLoading || isSaving}
            leftIcon={<SaveIcon className="h-4 w-4" />}
            className="shrink-0"
          >
            {isSaving ? t('common:actions.saving') : t('common:actions.save')}
          </Button>
        </div>
      )}

      <Card className="space-y-4">
        {/* Header */}
        <div>
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <SplitIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('expenses:sections.splitMethodSection')}
              </h3>
            </div>
            {numericAmount > 0 && (
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => setIsExpanded(false)}
                disabled={isLoading}
                leftIcon={<XIcon className="h-4 w-4" />}
                className="shrink-0"
              >
                {t('common:actions.close')}
              </Button>
            )}
          </div>

          {/* Split Method Selector */}
          {selector}
        </div>

        {/* Action buttons for percentage/custom */}
        <SplitAmountCalculator
          splitMethod={splitMethod}
          includedSplitsCount={includedSplits.length}
          calculationResult={calculationResult}
          onSplitEvenly={handleSplitEvenly}
          onAutoComplete={handleAutoComplete}
          isLoading={isLoading}
        />

        {/* Members list */}
        <SplitMemberList
          splits={splits}
          splitMethod={splitMethod}
          currency={currency}
          currentUserId={currentUserId}
          newSpotIds={newSpotIds}
          isLoading={isLoading}
          getEqualSplitAmount={getEqualSplitAmount}
          getSharesSplitAmount={getSharesSplitAmount}
          getEffectiveShares={getEffectiveShares}
          onIncludeToggle={handleIncludeToggle}
          onPercentageChange={handlePercentageChange}
          onPercentageSliderChange={handlePercentageSliderChange}
          onAmountChange={handleAmountChange}
          onSharesChange={handleSharesChange}
          numericAmount={numericAmount}
        />

        {saveSpot}

        {/* Allocation Summary Footer */}
        <SplitSummary
          splitMethod={splitMethod}
          currency={currency}
          numericAmount={numericAmount}
          calculationResult={calculationResult}
        />
      </Card>
    </div>
  );
}

export default UnifiedExpenseSplit;
